// src/pages/ContactPage.tsx
// Halaman kontak untuk pencarian beasiswa

import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, User, MessageSquare, Award, Youtube, Instagram } from 'lucide-react';
import DynamicPageHeader from '../components/DynamicPageHeader';
import Navbar from '../components/Navbar';
import { usePageHeader } from '../hooks/usePageHeader';
import { useToast } from '../components/admin/Toast';
import { useContactInfo } from '../hooks/useContactInfo';
import { useSocialLinks } from '../hooks/useSocialLinks';
import { supabase } from '../lib/supabase';
import { initEmailJS, handleContactEmailNotification } from '../services/emailService';

const TikTokIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.47V10.89a8.27 8.27 0 0 0 5.77 2.31v-3.45a4.85 4.85 0 0 1-3.77-3.06z" />
  </svg>
);



const ContactPage: React.FC = () => {
  const { success, error } = useToast();
  const { headerData, loading: headerLoading, error: headerError } = usePageHeader('contact');
  const { contactInfo, loading: contactLoading, error: contactError } = useContactInfo();
  const { socialLinks } = useSocialLinks();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize EmailJS when component mounts
  useEffect(() => {
    initEmailJS();
  }, []);

  if (headerLoading || contactLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="animate-pulse">
          <div className="h-96 bg-gray-300"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-300 rounded-lg"></div>
                ))}
              </div>
              <div className="h-96 bg-gray-300 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (headerError || contactError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
            <p className="text-gray-600">{headerError || contactError}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      error('Mohon lengkapi semua field yang wajib diisi');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Save message to database
      const { error: submitError } = await supabase
        .from('contact_messages')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            subject: formData.subject,
            message: formData.message,
            status: 'unread'
          }
        ]);

      if (submitError) throw submitError;
      
      // Send email notification
      try {
        const emailResult = await handleContactEmailNotification(formData);
        if (emailResult.notificationSent) {
          console.log('Email notification sent successfully');
        } else {
          console.warn('Email notification failed to send');
        }
      } catch (emailError) {
        console.error('Email notification error:', emailError);
        // Don't fail the entire process if email fails
      }
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      success('Pesan Anda berhasil dikirim! Saya akan merespon dalam waktu 24 jam.');
    } catch (err) {
      console.error('Error submitting contact form:', err);
      error('Gagal mengirim pesan. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 sm:pb-12">
        {/* Dynamic Header */}
        <div className="mb-12">
          <DynamicPageHeader
            title={headerData?.title || 'Hubungi Saya'}
            subtitle={headerData?.subtitle}
            description={headerData?.description}
            backgroundImageUrl={headerData?.background_image_url}
            loading={headerLoading}
            error={headerError}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Informasi Kontak
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email</h3>
                  <p className="text-gray-600">{contactInfo?.email || 'scholarship@madaportfolio.com'}</p>
                  <p className="text-sm text-gray-500">{contactInfo?.response_time || 'Respon dalam 24 jam'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Telepon</h3>
                  <p className="text-gray-600">{contactInfo?.phone || '+62 812-3456-7890'}</p>
                  <p className="text-sm text-gray-500">{contactInfo?.working_hours || 'Senin - Jumat, 09:00 - 17:00'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900">Alamat</h3>
                    <p className="text-gray-600">
                      {contactInfo?.address || 'Jl. Pendidikan No. 123, Jakarta Selatan, 12345'}
                    </p>
                  </div>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-3">Sosial Media Vano</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {socialLinks.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-medium text-sm transition-all hover:scale-105"
                  >
                    <Youtube className="h-5 w-5" />
                    <span>YouTube</span>
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a
                    href={socialLinks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-900 border border-gray-300 font-medium text-sm transition-all hover:scale-105"
                  >
                    <TikTokIcon className="h-5 w-5" />
                    <span>TikTok</span>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200 font-medium text-sm transition-all hover:scale-105"
                  >
                    <Instagram className="h-5 w-5" />
                    <span>Instagram</span>
                  </a>
                )}
              </div>
            </div>

            {/* Scholarship Types */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Award className="h-5 w-5 mr-2 text-blue-600" />
                Tentang Saya
              </h3>
              {contactInfo?.about_points && contactInfo.about_points.length > 0 ? (
                <ul className="space-y-2 text-sm text-gray-600">
                  {contactInfo.about_points.map((point, index) => (
                    <li key={index}>• {point}</li>
                  ))}
                </ul>
              ) : (
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Pelajar Berprestasi</li>
                  <li>• Aktif dalam Kompetisi</li>
                  <li>• Pengalaman Organisasi</li>
                  <li>• Minat di Bidang Teknologi</li>
                  <li>• Sedang Mencari Beasiswa</li>
                  <li>• Terbuka untuk Kolaborasi</li>
                </ul>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Formulir Kontak
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Lengkap *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="nama@email.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Nomor Telepon
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="08123456789"
                    />
                  </div>
                </div>


              </div>



              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subjek *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Pilih subjek</option>
                  {contactInfo?.form_subjects && contactInfo.form_subjects.length > 0 ? (
                    contactInfo.form_subjects.map((subject, index) => (
                      <option key={index} value={subject}>{subject}</option>
                    ))
                  ) : (
                    <>
                      <option value="kolaborasi">Kolaborasi</option>
                      <option value="penawaran-beasiswa">Penawaran Beasiswa</option>
                      <option value="informasi-portfolio">Informasi Portfolio</option>
                      <option value="lainnya">Lainnya</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Pesan *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Sampaikan pesan Anda..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Mengirim...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Kirim Pesan</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;